/**
 * Page-provided breadcrumb trail for the header (Figma Admin 3.0 «Navbar»).
 *
 * The header shows the current page's sidebar label by default. A page that
 * needs a deeper trail (e.g. Reports › member name on /users/user) publishes
 * its steps here, keyed by the pathname they belong to, so a trail set on one
 * route never shows on another even if the page forgets to clear it.
 *
 * Plain module pub/sub (no React, no DataController import), like
 * notifications.store, so `DataController.clearSession()` can reset it.
 */
const listeners = new Set();

const EMPTY = Object.freeze({ pathname: null, steps: null });
let snapshot = EMPTY;

function emit() {
    listeners.forEach((listener) => {
        try {
            listener(snapshot);
        } catch (error) {
            console.error('breadcrumb.store listener failed', error);
        }
    });
}

/** @returns {{pathname: string|null, steps: Array<object>|null}} */
export function getBreadcrumbSnapshot() {
    return snapshot;
}

/**
 * @param {string} pathname the route the steps belong to
 * @param {Array<object>} steps design-elements `Breadcrumb` steps
 */
export function setBreadcrumb(pathname, steps) {
    snapshot = { pathname: pathname || null, steps: Array.isArray(steps) ? steps : null };
    emit();
}

/**
 * The steps published for `pathname`, or null when none were (or they belong
 * to another route).
 *
 * @param {string} pathname
 * @param {{pathname: string|null, steps: Array<object>|null}} [from] a snapshot to read
 *   (e.g. one a component holds in state); defaults to the current one
 * @returns {Array<object>|null}
 */
export function getBreadcrumbFor(pathname, from = snapshot) {
    return from?.pathname && from.pathname === pathname ? from.steps : null;
}

/**
 * @param {(snapshot: {pathname: string|null, steps: Array<object>|null}) => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeToBreadcrumb(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** Called by the page that set it when it unmounts, and from DataController.clearSession() on logout. */
export function resetBreadcrumb() {
    snapshot = EMPTY;
    emit();
}
